import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./OrderPages.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import AutoRefreshBar from "./AutoRefreshBar";
import { useSyncedOrderPagesAutoRefresh } from "./orderPagesAutoRefresh";
import usePageTitle from "./usePageTitle";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatBytes = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "N/A";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
};

function WorkerLinesIndexPage() {
  usePageTitle("Worker Line Logs");
  const [lines, setLines] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useSyncedOrderPagesAutoRefresh();
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);
  const isMountedRef = useRef(true);

  const fetchIntervalMs = useMemo(() => {
    const env = process.env.REACT_APP_FETCH_INTERVAL_MS;
    const parsed = Number(env);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchLines = useCallback(async () => {
    try {
      const response = await auth.fetchWithAuth("/worker_lines/");
      if (response.status === 404) {
        if (isMountedRef.current) {
          setLines([]);
          setError("Per-order file logging is disabled (set WORKER_LOG_TO_FILE=1).");
        }
        return;
      }
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setLines(Array.isArray(payload?.lines) ? payload.lines : []);
        setError("");
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [auth.fetchWithAuth]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) return;
    let intervalId = null;
    fetchLines();
    if (autoRefresh) intervalId = setInterval(fetchLines, fetchIntervalMs);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [auth.authChecked, auth.isAuthenticated, autoRefresh, fetchIntervalMs, fetchLines]);

  return (
    <AuthGate auth={auth}>
      <div className="order-page">
        <div className="order-container">
          <h1 className="order-title">Worker line logs</h1>
          <AutoRefreshBar
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            fetchIntervalMs={fetchIntervalMs}
            onRefreshNow={fetchLines}
            subtitleWhenOn={`Lista odświeża się co ${Math.round(fetchIntervalMs / 1000)} s`}
            subtitleWhenOff="Odświeżanie co interwał jest wyłączone"
          />
          {error && <div className="order-error">{error}</div>}
          {loading ? (
            <div className="order-loader">
              <div className="order-spinner" />
              <p>Loading worker lines...</p>
            </div>
          ) : lines.length === 0 ? (
            <div>No worker lines with logs.</div>
          ) : (
            <table className="order-table">
              <thead>
                <tr>
                  <th>Worker line</th>
                  <th>Orders</th>
                  <th>Total size</th>
                  <th>Oldest log</th>
                  <th>Newest log</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.line_no}>
                    <td>
                      <a
                        href={`/order_logs/${encodeURIComponent(line.line_no)}`}
                        className="order-link"
                      >
                        worker_line_{line.line_no}
                      </a>
                    </td>
                    <td>{line.order_count}</td>
                    <td>{formatBytes(line.total_size_bytes)}</td>
                    <td>{formatDateTime(line.oldest_log)}</td>
                    <td>{formatDateTime(line.newest_log)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

export default WorkerLinesIndexPage;
